import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Checkbox } from 'antd';
import { useSelector } from 'react-redux';
import type { RootState } from '@@store/index';
import { Button } from '@@components';
import './index.less';

export interface DamageFormValues {
    name: string;
    phone: string;
    email: string;
    serialNumber: string;
    address: string;
    typeAndModel: string;
    howHappened: string;
    whoCaused: string;
    damagedParts: string;
    date: string;
    signature: string;
}

export interface DamageFormPrefill {
    name?: string;
    phone?: string;
    email?: string;
    serialNumber?: string;
    address?: string;
    typeAndModel?: string;
    howHappened?: string;
    whoCaused?: string;
    damagedParts?: string;
}

export interface DamageFormProps {
    open: boolean;
    prefill?: DamageFormPrefill;
    title?: string;
    description?: string;
    readonlyFields?: string[];
    requiredFields?: string[];
    agreementRequired?: boolean;
    agreementText?: string;
    cancelConfirmText?: string;
    onClose: () => void;
    onSave?: (values: DamageFormValues) => void; // eslint-disable-line no-unused-vars
}

const DEFAULT_TITLE = 'Lenovo Accidental Damage Protection (insurance) Form';
const DEFAULT_DESC = 'Completed Lenovo Accidental Damage Protection (insurance) Form must always be submitted to your authorized Lenovo service provider prior any accidental damage repair can commence.';
const DEFAULT_READONLY = [ 'name', 'phone', 'email', 'serialNumber', 'address', 'typeAndModel' ];
const DEFAULT_REQUIRED = [ 'name', 'phone' ];
const DEFAULT_AGREEMENT_TEXT = 'I understand the responsibilities I have and that damage caused by misuse or abuse is not covered by the Lenovo warranty or Accidental Damage Protection (insurance).';
const DEFAULT_CONFIRM_TEXT = 'Completing the form helps us better understand your issue. Would you like to continue filling it out?';

const DEFAULT_PREFILL: Required<DamageFormPrefill> = {
    name: 'Evil Rabbit',
    phone: '112313131812',
    email: 'Evil Rabbit@acme.com',
    serialNumber: 'SN12371928735913',
    address: '',
    typeAndModel: '',
    howHappened: '',
    whoCaused: '',
    damagedParts: ''
};

const { TextArea } = Input;

const DamageForm: React.FC<DamageFormProps> = ({
    open,
    prefill,
    title,
    description,
    readonlyFields,
    requiredFields,
    agreementRequired,
    agreementText,
    cancelConfirmText,
    onClose,
    onSave
}) => {
    const curTheme = useSelector((state: RootState) => state.theme);
    const [ form ] = Form.useForm<DamageFormValues>();
    const [ confirmOpen, setConfirmOpen ] = useState(false);
    const [ agreed, setAgreed ] = useState(false);

    const todayStr = () => {
        const d = new Date();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${mm}-${dd}`;
    };

    const readonlySet = new Set(readonlyFields && readonlyFields.length > 0 ? readonlyFields : DEFAULT_READONLY);
    const requiredSet = new Set(requiredFields && requiredFields.length > 0 ? requiredFields : DEFAULT_REQUIRED);
    const isReadonly = (k: string) => readonlySet.has(k);
    const isRequired = (k: string) => requiredSet.has(k);
    const renderLabel = (k: string, label: string) => (
        isRequired(k)
            ? <span><span className="required-mark">*</span>{label}</span>
            : <>{label}</>
    );

    useEffect(() => {
        if (open) {
            setAgreed(false);
            const merged = { ...DEFAULT_PREFILL, ...(prefill || {}) };
            form.setFieldsValue({
                name: merged.name,
                phone: merged.phone,
                email: merged.email,
                serialNumber: merged.serialNumber,
                address: merged.address,
                typeAndModel: merged.typeAndModel,
                howHappened: merged.howHappened,
                whoCaused: merged.whoCaused,
                damagedParts: merged.damagedParts,
                date: todayStr(),
                signature: ''
            });
        }
    }, [ open, prefill, form ]);

    // 监听所有字段以驱动 Save 按钮禁用状态
    const watched = Form.useWatch([], form) as Partial<DamageFormValues> | undefined;

    const isFilled = (k: keyof DamageFormValues): boolean => {
        const v = watched?.[k];
        return typeof v === 'string' ? v.trim().length > 0 : !!v;
    };
    const requiredFilled = Array.from(requiredSet).every((k) => isFilled(k as keyof DamageFormValues));
    const agreementOk = agreementRequired === false || agreed;
    const canSave = requiredFilled && agreementOk;

    const handleSave = async () => {
        if (!canSave) return;
        const values = form.getFieldsValue(true);
        onSave?.(values as DamageFormValues);
    };

    const handleCloseAttempt = () => {
        setConfirmOpen(true);
    };

    return (
        <>
            <Modal
                open={open}
                onCancel={handleCloseAttempt}
                centered
                width={640}
                footer={null}
                destroyOnHidden
                maskClosable={false}
                wrapClassName={`damage-form-modal ${curTheme}-damage-form-modal`}
                aria-labelledby="damage-form-title"
            >
                <div className="damage-form-header">
                    <h2 id="damage-form-title" className="damage-form-title">{title || DEFAULT_TITLE}</h2>
                    <p className="damage-form-desc">{description || DEFAULT_DESC}</p>
                </div>

                <Form form={form} layout="vertical" className="damage-form-body" requiredMark={false}>
                    <div className="damage-form-row">
                        <Form.Item label={renderLabel('name', 'Name')} name="name" className="damage-form-col">
                            <Input readOnly={isReadonly('name')} />
                        </Form.Item>
                        <Form.Item label={renderLabel('phone', 'Phone')} name="phone" className="damage-form-col">
                            <Input readOnly={isReadonly('phone')} />
                        </Form.Item>
                    </div>

                    <div className="damage-form-row">
                        <Form.Item label={renderLabel('email', 'Email Address')} name="email" className="damage-form-col">
                            <Input readOnly={isReadonly('email')} />
                        </Form.Item>
                        <Form.Item label={renderLabel('serialNumber', 'Serial Number-SN')} name="serialNumber" className="damage-form-col">
                            <Input readOnly={isReadonly('serialNumber')} />
                        </Form.Item>
                    </div>

                    <Form.Item label={renderLabel('address', 'Address')} name="address">
                        <Input readOnly={isReadonly('address')} />
                    </Form.Item>

                    <Form.Item label={renderLabel('typeAndModel', 'Type and Model number-MTM')} name="typeAndModel">
                        <Input readOnly={isReadonly('typeAndModel')} />
                    </Form.Item>

                    <Form.Item label={renderLabel('howHappened', 'How did the accident happen?')} name="howHappened">
                        <TextArea rows={3} maxLength={1000} readOnly={isReadonly('howHappened')} />
                    </Form.Item>

                    <Form.Item label={renderLabel('whoCaused', 'Who caused the damage?')} name="whoCaused">
                        <TextArea rows={3} maxLength={1000} readOnly={isReadonly('whoCaused')} />
                    </Form.Item>

                    <Form.Item label={renderLabel('damagedParts', 'What parts appear to be damaged?')} name="damagedParts">
                        <TextArea rows={3} maxLength={1000} readOnly={isReadonly('damagedParts')} />
                    </Form.Item>

                    {agreementRequired !== false && (
                        <Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="damage-form-agreement">
                            <span className="required-mark">*</span>
                            {agreementText || DEFAULT_AGREEMENT_TEXT}
                        </Checkbox>
                    )}

                    <div className="damage-form-row damage-form-sign-row">
                        <Form.Item label="Date" name="date" className="damage-form-col">
                            <Input placeholder="YYYY-MM-DD" />
                        </Form.Item>
                        <Form.Item label="Signature" name="signature" className="damage-form-col">
                            <Input placeholder="Your signature" />
                        </Form.Item>
                    </div>
                </Form>

                <div className="damage-form-footer">
                    <Button className="damage-form-cancel" onClick={handleCloseAttempt}>Cancel</Button>
                    <Button
                        type="primary"
                        className="damage-form-save"
                        onClick={handleSave}
                        disabled={!canSave}
                    >
                        Save
                    </Button>
                </div>
            </Modal>

            <Modal
                open={confirmOpen}
                centered
                footer={null}
                closable={false}
                maskClosable={false}
                width={400}
                wrapClassName={`damage-form-confirm-modal ${curTheme}-damage-form-confirm-modal`}
                zIndex={1001}
                aria-labelledby="damage-form-confirm-title"
            >
                <p id="damage-form-confirm-title" className="damage-form-confirm-text">
                    {cancelConfirmText || DEFAULT_CONFIRM_TEXT}
                </p>
                <div className="damage-form-confirm-footer">
                    <Button type="primary" onClick={() => setConfirmOpen(false)}>Yes</Button>
                    <Button onClick={() => { setConfirmOpen(false); setTimeout(onClose, 200); }}>No</Button>
                </div>
            </Modal>
        </>
    );
};

export default DamageForm;
